import cx from "clsx";
import { Artwork } from "kreate-common/modules/business-types";
import ButtonOutline from "kreate-common/modules/kreate-ui/components/ButtonOutline";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { httpGet$GetComments } from "kreate-server/commands/GetComments/fetcher";
import {
  GetComments$Params,
  GetComments$Result,
} from "kreate-server/commands/GetComments/typing";
import React from "react";
import useSWRInfinite from "swr/infinite";

import CommentComposer from "./components/CommentComposer";
import CommentViewer from "./components/CommentViewer";
import styles from "./index.module.scss";

import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";

type LoadEvent = {
  totalNumComments?: number;
};

export type TabComments$LoadEvent = LoadEvent;

type Props = {
  className?: string;
  style?: React.CSSProperties;
  artwork: Artwork | null | undefined;
  __referentiallyStable__onLoad?: (event: LoadEvent) => void;
};

export default function TabComments({
  className,
  style,
  artwork,
  __referentiallyStable__onLoad: onLoad,
}: Props) {
  const loginStatus = useLoginStatus();

  const ITEMS_PER_PAGE = 12;

  const commentsSWR = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (!artwork) {
        return undefined;
      }
      if (pageIndex) {
        const previousResultSP = GetComments$Result.safeParse(previousPageData);
        if (
          !previousResultSP.success ||
          !previousResultSP.data.comments.length
        ) {
          return undefined;
        }
      }

      const params: GetComments$Params = {
        offset: pageIndex * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
        type: "artwork",
        targetId: artwork?.artworkId,
      };
      return ["352df332-537a-4b26-8307-c74bb23886c5", params];
    },
    async ([_, params]) =>
      await httpGet$GetComments("/api/v1/all-comments", params)
  );

  const totalNumComments = commentsSWR?.data?.[0]?.count;

  React.useEffect(() => {
    onLoad?.({ totalNumComments });

    return () => {
      onLoad?.({ totalNumComments: undefined });
    };
  }, [onLoad, totalNumComments]);

  const hasMore = (() => {
    if (!commentsSWR.data?.length) return true;
    const lastResult = commentsSWR.data[commentsSWR.data.length - 1];
    return lastResult.comments.length >= ITEMS_PER_PAGE;
  })();

  const revalidateEverything = async () => {
    await commentsSWR.mutate();
  };

  return (
    <div className={cx(styles.container, className)} style={style}>
      <Flex.Col>
        {loginStatus?.loggedIn ? (
          <CommentComposer
            className={styles.commentComposer}
            artwork={artwork}
            revalidateEverything={revalidateEverything}
          />
        ) : null}
        <Flex.Col>
          {commentsSWR.data?.map((getComments$Result, index) => (
            <React.Fragment key={index}>
              {getComments$Result.comments.map((comment) => (
                <CommentViewer
                  key={comment.commentId}
                  comment={comment}
                  revalidateEverything={revalidateEverything}
                />
              ))}
            </React.Fragment>
          ))}
        </Flex.Col>
        <Flex.Row paddingTop="24px" alignItems="center" justifyContent="center">
          {hasMore ? (
            <ButtonOutline
              content="Load More"
              onClick={() => commentsSWR.setSize((size) => size + 1)}
            />
          ) : totalNumComments ? (
            <div className={styles.textNoMoreComments}>
              {"No more comments"}
            </div>
          ) : undefined}
        </Flex.Row>
      </Flex.Col>
    </div>
  );
}
